import { Button, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { UserInfoBlock, UserInfoRow } from './SpaceMembersModal';
import { WorkspaceAccessResponse } from '../../../api/workspace';
import { useGetCurrentWorkspaceUsers, useGetWorkspace } from '../../../serverState/workspace';
import { User } from '../../../types/User';
import { ArrowLeftOutlined, InfoCircleFilled, UserAddOutlined } from '@ant-design/icons';
import { useGetOrganizationInvites, useGetOrganizationUsers } from '../../../serverState/organization';
import { OrganizationUsersResponse, USER_STATE_IN_ORGANIZATION, UserInvitation, getOrganizationUsers } from '../../../api/organization';
import { useUpdateUserSpaceRoleMutation } from '../../../serverState/user';
import TagInput, { EmailType, TagInputRef } from '../../../components/TagInput/TagInput';
import { inviteUsersToOrganization } from '../../../api/user';
import {
  OrganizationAccessRoles,
  getCurrentlyActiveOrganization,
  useGetUserFromAccessToken,
} from '../../../common/auth';
import { extractDomain, isCompanyEmail, isTemporaryEmail, isValidEmail } from '../../../common/utils';
import colorTokens from '../../../constants/design_system/colors';
import CustomTooltip from '../../../components/UI/Tooltip/CustomTooltip';

export type InviteToWorkspaceProps = {
  onBackClick: () => void;
  currentUserSpaceAccess: WorkspaceAccessResponse | null;
  onCancel: () => void;
};

export const InviteToWorkspace = ({ onBackClick, currentUserSpaceAccess, onCancel }: InviteToWorkspaceProps) => {
  const workspaceQuery = useGetWorkspace();
  const mutation = useUpdateUserSpaceRoleMutation(workspaceQuery.data?.id);

  // get workspaceAccess so we know who to show as already in workspace
  const workspaceUsersQuery = useGetCurrentWorkspaceUsers();

  // get org users so we know who to show as not in workspace
  const orgUsersQuery = useGetOrganizationUsers();

  const organisationInvitesQuery = useGetOrganizationInvites();

  const currentUser = useGetUserFromAccessToken();

  const [searchValue, setsearchValue] = useState('');
  const [isInviteDisabled, setIsInviteDisabled] = useState(true);
  const tagInputRef = useRef<TagInputRef>(null);
  const [usersToInvite, setUsersToInvite] = useState<string[]>([]);
  const [messageApi, contextHolder] = message.useMessage();

  const onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setsearchValue(e.target.value);
  };

  const [filteredWorkspaceUsers, setfilteredWorkspaceUsers] = useState<WorkspaceAccessResponse[]>([]);
  useEffect(() => {
    if (workspaceUsersQuery.data) {
      if (searchValue === '') {
        setfilteredWorkspaceUsers(workspaceUsersQuery.data);
        return;
      }

      const filtered = workspaceUsersQuery.data.filter((workspaceAccess) => {
        const user = workspaceAccess.User as Partial<User>;
        if (user.fullName?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        if (user.email?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        if (workspaceAccess.role?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        return false;
      });
      setfilteredWorkspaceUsers(filtered);
    }
  }, [searchValue, workspaceUsersQuery.status, workspaceUsersQuery.data, orgUsersQuery.status, orgUsersQuery.data]);

  // Combined type for "Not in Space" section - org users and pending invites together
  type NotInSpaceItem =
    | { type: 'user'; data: OrganizationUsersResponse }
    | { type: 'invite'; data: UserInvitation };

  const [filteredNotInSpace, setFilteredNotInSpace] = useState<NotInSpaceItem[]>([]);
  const [filteredPendingInvitesInSpace, setFilteredPendingInvitesInSpace] = useState<UserInvitation[]>([]);

  useEffect(() => {
    if (!orgUsersQuery.data || !organisationInvitesQuery.data) {
      return;
    }

    // Filter org users not in space
    const filteredOrgUsers: NotInSpaceItem[] = orgUsersQuery.data
      .filter((orgUser) => {
        // only show active members
        if (orgUser.usersStateInOrganization !== USER_STATE_IN_ORGANIZATION.ACTIVE) {
          return false;
        }

        const user = orgUser.User as Partial<User>;

        // if user already in workspace, don't show
        if (workspaceUsersQuery.data?.findIndex((workspaceUser) => workspaceUser.User.id === user.id) !== -1) {
          return false;
        }

        // if user already in invite list, don't show
        if (user.email && usersToInvite.includes(user.email)) {
          return false;
        }

        // if user already has a pending invitation to this space, don't show
        if (user.email && organisationInvitesQuery.data) {
          const hasPendingInvite = organisationInvitesQuery.data.some(
            (invite) =>
              invite.email === user.email &&
              invite.Workspaces?.some((ws) => ws.workspaceId === workspaceQuery.data?.id)
          );
          if (hasPendingInvite) {
            return false;
          }
        }

        if (searchValue === '') {
          return true;
        }

        if (user.fullName?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        if (user.email?.toLowerCase().includes(searchValue.toLowerCase())) {
          return true;
        }
        return false;
      })
      .map((orgUser) => ({ type: 'user' as const, data: orgUser }));

    // Filter pending invites - split into "not in space" and "in space"
    const pendingNotInSpace: NotInSpaceItem[] = [];
    const pendingInSpace: UserInvitation[] = [];

    organisationInvitesQuery.data.forEach((invite) => {
      // Check if already in current invite list
      if (usersToInvite.includes(invite.email)) {
        return;
      }

      // Apply search filter
      if (searchValue !== '' && !invite.email?.toLowerCase().includes(searchValue.toLowerCase())) {
        return;
      }

      // Check if this invite has access to the current space
      const hasSpaceAccess = invite.Workspaces?.some((ws) => ws.workspaceId === workspaceQuery.data?.id);

      if (hasSpaceAccess) {
        pendingInSpace.push(invite);
      } else {
        pendingNotInSpace.push({ type: 'invite' as const, data: invite });
      }
    });

    // Combine org users and pending invites for "Not in Space" section
    const combinedNotInSpace = [...filteredOrgUsers, ...pendingNotInSpace];

    // Sort combined list: 1. A-Z by name/email, 2. Earlier joiners on top
    const sortedNotInSpace = combinedNotInSpace.sort((a, b) => {
      // Get name or email for comparison
      const getNameOrEmail = (item: NotInSpaceItem) => {
        if (item.type === 'user') {
          return (item.data.User.fullName || item.data.User.email || '').toLowerCase();
        }
        return (item.data.email || '').toLowerCase();
      };

      const nameA = getNameOrEmail(a);
      const nameB = getNameOrEmail(b);
      const nameComparison = nameA.localeCompare(nameB);

      if (nameComparison !== 0) {
        return nameComparison;
      }

      // Secondary: earlier createdAt on top
      const dateA = new Date(a.data.createdAt).getTime();
      const dateB = new Date(b.data.createdAt).getTime();
      return dateA - dateB;
    });

    setFilteredNotInSpace(sortedNotInSpace);

    // Sort "In Space" pending invites
    const sortedInSpace = pendingInSpace.sort((a, b) => {
      const emailComparison = (a.email || '').toLowerCase().localeCompare((b.email || '').toLowerCase());
      if (emailComparison !== 0) {
        return emailComparison;
      }
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });

    setFilteredPendingInvitesInSpace(sortedInSpace);
  }, [searchValue, orgUsersQuery.data, workspaceUsersQuery.data, usersToInvite, organisationInvitesQuery.data, workspaceQuery.data?.id]);

  // Centralized email validation logic
  // Returns { error, shouldClearInput } to handle different validation cases
  const validateEmailForInvite = (email: string): { error: string | null; shouldClearInput: boolean } => {
    if (!email || email.trim() === '') {
      return { error: null, shouldClearInput: false };
    }

    if (!isValidEmail(email)) {
      return { error: 'Invalid email format. Try again.', shouldClearInput: false };
    }

    if (isTemporaryEmail(email)) {
      return { error: 'Emails from temporary public domains are not allowed.', shouldClearInput: false };
    }

    // Check if current user has public email (gmail) and is not admin
    // They can only invite existing org members, not external users
    const isUserAdmin = orgAccess && orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN;
    const hasPublicEmail = currentUser.data?.user.email && !isCompanyEmail(currentUser.data.user.email);

    if (hasPublicEmail && !isUserAdmin) {
      // Check if the email exists in the organization
      const isExistingOrgMember = orgUsersQuery.data?.some((orgUser) => orgUser.User.email === email);
      const isExistingOrgInvite = organisationInvitesQuery.data?.some((invite) => invite.email === email);

      if (!isExistingOrgMember && !isExistingOrgInvite) {
        return { error: 'Only organization admins can invite users with other domain email addresses.', shouldClearInput: false };
      }
    }

    if (!isAllowedDomain(email)) {
      // Check if the invited domain is another org domain (used by other org members)
      const invitedDomain = extractDomain(email);
      const isOtherOrgDomain = orgUsersQuery.data?.some(
        (orgUser) => extractDomain(orgUser.User.email) === invitedDomain
      );

      if (isOtherOrgDomain) {
        return { error: 'Only organization admins can invite users with other domain email addresses.', shouldClearInput: false };
      } else {
        return { error: 'Only organization admins can invite users with external domain email addresses.', shouldClearInput: false };
      }
    }

    // Cases that should clear input
    if (usersToInvite.includes(email)) {
      return { error: 'This email is already on the invitation list.', shouldClearInput: true };
    }

    if (isAlreadyAdded(email)) {
      return { error: 'User with this email is already in the space.', shouldClearInput: true };
    }

    if (isAlreadyInvitedPending(email)) {
      return { error: 'User with this email already has a pending invitation to this space.', shouldClearInput: true };
    }

    if (isDeactivatedUser(email)) {
      return { error: 'Can not invite deactivated users to the space.', shouldClearInput: true };
    }

    return { error: null, shouldClearInput: false };
  };

  // This will always get a list of all items currently in the select list.
  const handleChange = (values: string[]) => {
    if (values.length === 0) {
      setUsersToInvite([]);
      setIsInviteDisabled(true);
      tagInputRef.current?.focus();
      return;
    }

    // If values length is less than or equal to current, it's a deletion - just update without validation
    if (values.length <= usersToInvite.length) {
      setUsersToInvite([...values]);
      setIsInviteDisabled(!values.reduce((acc, curr) => acc && isAllowedDomain(curr), true));
      tagInputRef.current?.focus();
      return;
    }

    const value = values.slice(-1)[0];

    if (value.length === 0) {
      return;
    }

    // Use centralized validation
    const validation = validateEmailForInvite(value);
    if (validation.error) {
      void messageApi.error(validation.error);
      if (validation.shouldClearInput) {
        setsearchValue('');
      }
      return;
    }

    setUsersToInvite([...values]);
    setIsInviteDisabled(!values.reduce((acc, curr) => acc && isAllowedDomain(curr), true));
    setsearchValue('');
  };

  const handleInviteSubmit = async (emails: string[]) => {
    try {
      const usersToInvite = emails.map((email) => ({
        email,
        role: OrganizationAccessRoles.MEMBER,
      }));
      const invReq = await inviteUsersToOrganization(usersToInvite, workspaceQuery.data?.id);

      // Use global message (not messageApi) so toast persists after modal closes
      void message.success('Invitation(s) successfully sent.');

      // fetch the invites and the list of users
      const currentOrg = await getCurrentlyActiveOrganization();

      const usersRes = await getOrganizationUsers(currentOrg.id);

      workspaceQuery.refetch();
      orgUsersQuery.refetch();
      workspaceUsersQuery.refetch();
      organisationInvitesQuery.refetch();

      onCancel();
    } catch (e: any) {
      const errorCode = e?.response?.data?.code;
      const errorMessage = e?.response?.data?.message;

      if (errorCode === 'ERR-USER-33') {
        void messageApi.error('Only admins can invite other admins');
      } else if (errorCode === 'ERR-USER-34') {
        void messageApi.error('Please provide valid user information');
      } else if (errorCode === 'ERR-USER-35') {
        void messageApi.error('Invalid role specified');
      } else {
        void messageApi.error(errorMessage || e?.response?.data?.error || 'Failed to invite users.');
      }
      tagInputRef.current?.focus();
    }
  };

  const handleSendInvites = () => {
    // If there's text in the input, validate and try to add it first
    if (searchValue && searchValue.trim()) {
      const validation = validateEmailForInvite(searchValue);
      if (validation.error) {
        void messageApi.error(validation.error);
        if (validation.shouldClearInput) {
          setsearchValue('');
        }
        tagInputRef.current?.focus();
        return;
      }

      // Valid! Add it and submit with the complete list
      const finalList = [...usersToInvite, searchValue];
      void handleInviteSubmit(finalList);
    } else {
      // No pending input, submit current list
      void handleInviteSubmit(usersToInvite);
    }
  };

  const domain = extractDomain(currentUser.data?.user.email || '');
  const orgAccess = orgUsersQuery.data?.find((orgAccess) => orgAccess.userId === currentUser.data?.user.id);
  const isAllowedDomain = (email: string) => {
    if (currentUser.data?.user.email && !isCompanyEmail(currentUser.data.user.email)) {
      return true;
    }
    if (orgAccess && orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN) {
      return true;
    }
    return domain === extractDomain(email);
  };

  const isAlreadyAdded = (email: string) => {
    const spaceAccess = workspaceUsersQuery.data?.find((spaceAccess) => spaceAccess.User.email === email);
    return !!spaceAccess;
  };

  const isAlreadyInvitedPending = (email: string) => {
    const invite = organisationInvitesQuery.data?.find(
      (invite) =>
        invite.email === email &&
        invite.Workspaces?.some((ws) => ws.workspaceId === workspaceQuery.data?.id)
    );
    return !!invite;
  };

  const isDeactivatedUser = (email: string) => {
    const orgUser = orgUsersQuery.data?.find((orgUser) => orgUser.User.email === email);
    return orgUser?.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.INACTIVE;
  };

  const emailType = (email: string): EmailType => {
    if (!currentUser.data?.user.email) {
      return EmailType.sameDomain;
    }
    if (orgUsersQuery.data?.find((orgAccess) => orgAccess.User.email === email)) {
      return EmailType.orgUser;
    }
    if (isCompanyEmail(currentUser.data.user.email)) {
      return extractDomain(currentUser.data.user.email) === extractDomain(email)
        ? EmailType.sameDomain
        : EmailType.differentDomain;
    }

    return EmailType.differentDomain;
  };

  return (
    <>
      {contextHolder}
      <div className="flex flex-col gap-5 pt-3">
        <div className="pl-5 pr-5 flex flex-col gap-5">
          <div>
            <div
              className="inline-flex text-base font-semibold cursor-pointer w-auto"
              onClick={() => {
                onBackClick();
              }}
            >
              <ArrowLeftOutlined className="mr-1" /> {'Invite to Teamspace'}
            </div>
          </div>
          <div>
            {/* <Input
                        placeholder="Search people or emails"
                        value={searchValue}
                        onChange={onSearchInput}
                    /> */}
            <TagInput
              ref={tagInputRef}
              value={usersToInvite}
              inputValue={searchValue}
              emailType={emailType}
              onChange={handleChange}
              onTextContentChange={(e) => {
                setsearchValue(e);
              }}
              placeholder="Enter email to invite"
            />
          </div>
        </div>
        <div className="main-content h-[318px] overflow-y-auto" style={{ boxShadow: 'inset 0 -12px 12px -12px rgba(0, 0, 0, 0.1)' }}>
          {/* Not in Space section */}
          {filteredNotInSpace.length > 0 && (
            <div className="text-neutral-900 text-xs font-semibold pl-5">Not in Space</div>
          )}
          <div className="mt-[6px] mb-4">
            {filteredNotInSpace.map((item) =>
              item.type === 'user' ? (
                <div key={`orgaccess-${item.data.id}`}>
                  <OrgUserInfoRow
                    access={item.data}
                    inviteUser={(email) => {
                      if (email && !usersToInvite.includes(email)) {
                        setUsersToInvite([...usersToInvite, email]);
                        setsearchValue(''); // Clear search input after adding user
                        tagInputRef.current?.focus();
                      }
                    }}
                  />
                </div>
              ) : (
                <div key={`pending-notinspace-${item.data.id}`}>
                  <PendingInviteRow
                    invitation={item.data}
                    inviteUser={(email) => {
                      if (email && !usersToInvite.includes(email)) {
                        setUsersToInvite([...usersToInvite, email]);
                        setsearchValue('');
                        tagInputRef.current?.focus();
                      }
                    }}
                  />
                </div>
              )
            )}
          </div>

          {/* In Space section */}
          {(filteredWorkspaceUsers.length > 0 || filteredPendingInvitesInSpace.length > 0) && (
            <div className="text-neutral-900 text-xs font-semibold pl-5 sticky top-0 z-[1]">
              In Space
            </div>
          )}
          <div className="mt-[6px]">
            {filteredWorkspaceUsers.map((workspaceAccess) => {
              return (
                <div key={`workspaceaccess-${workspaceAccess.id}`} className="">
                  <UserInfoRow
                    currentUserSpaceAccess={currentUserSpaceAccess}
                    access={workspaceAccess}
                    showRoleAsText={true}
                  />
                </div>
              );
            })}
            {filteredPendingInvitesInSpace.map((invite) => {
              return (
                <div key={`pending-inspace-${invite.id}`}>
                  <PendingInviteInSpaceRow invitation={invite} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between">
          <div className="flex space-x-2">
            <InfoCircleFilled style={{ color: colorTokens.neutral[800] }} className="pb-4" />
            <div className="w-80 text-neutral-900 text-xs font-normal font-['Figtree'] leading-none">
              Only organization admins can invite users with external domain email addresses.
            </div>
          </div>

          <Button
            type="primary"
            className="pl-2 pr-2 "
            onClick={handleSendInvites}
            disabled={usersToInvite.length === 0 && !searchValue.trim()}
          >
            Send invites
          </Button>
        </div>
      </div>
    </>
  );
};

export const OrgUserInfoRow = ({
  access,
  inviteUser,
}: {
  access: Partial<OrganizationUsersResponse>;
  inviteUser: (email: string | undefined) => void;
}) => {
  const onAddUserToWorkspace = () => {
    inviteUser(access.User?.email);
  };

  return (
    <div
      className="flex justify-between gap-2 items-center pt-2 pb-2 pl-5 pr-5 cursor-pointer hover:bg-neutral-300 min-w-0"
      onClick={onAddUserToWorkspace}
    >
      <div className="flex-1 min-w-0">
        <UserInfoBlock user={access.User as User} />
      </div>

      <CustomTooltip title="Add Member">
        <UserAddOutlined style={{ color: 'var(--color-neutral-900)', fontSize: 20 }} className="pr-3" />
      </CustomTooltip>
    </div>
  );
};

// Pending invite row for "Not in Space" section (clickable)
export const PendingInviteRow = ({
  invitation,
  inviteUser,
}: {
  invitation: Partial<UserInvitation>;
  inviteUser: (email: string | undefined) => void;
}) => {
  const onAddToInviteList = () => {
    inviteUser(invitation.email);
  };

  return (
    <div
      className="flex justify-between gap-2 items-center pt-2 pb-2 pl-5 pr-5 cursor-pointer hover:bg-neutral-300 min-w-0"
      onClick={onAddToInviteList}
    >
      <div className="flex-1 min-w-0">
        <UserInfoBlock invitation={invitation} />
      </div>

      <CustomTooltip title="Add Member">
        <UserAddOutlined style={{ color: 'var(--color-neutral-900)', fontSize: 20 }} className="pr-3" />
      </CustomTooltip>
    </div>
  );
};

// Pending invite row for "In Space" section (not clickable, already has space access)
export const PendingInviteInSpaceRow = ({
  invitation,
}: {
  invitation: Partial<UserInvitation>;
}) => {
  return (
    <div className="flex justify-between gap-2 items-center pt-2 pb-2 pl-5 pr-5 min-w-0">
      <div className="flex-1 min-w-0">
        <UserInfoBlock invitation={invitation} />
      </div>
      <span className="text-sm text-neutral-900 pr-3">Member</span>
    </div>
  );
};
